import React from 'react';

const KesslerShowcase = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Subtle Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full filter blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <header className="relative py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full">
            <span className="text-blue-300 text-sm font-medium">📊 Data Visualization Project</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Kessler Syndrome
            </span>
          </h1>
          <h2 className="text-2xl md:text-3xl text-gray-300 mb-6">Humanity's Prison</h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Interactive visualization exploring space debris growth and the cascading collision 
            scenario that could trap humanity on Earth for generations
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="https://814-data.streamlit.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-lg transition-all font-semibold shadow-lg shadow-blue-500/30"
            >
              <span>Launch Dashboard</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <a
              href="https://github.com/ARNAB814/Data_Visualization_Project"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 px-6 py-3 rounded-lg transition-all"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
              </svg>
              <span>Source Code</span>
            </a>
          </div>

          {/* Award */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-purple-500/20 border border-purple-500/30 px-4 py-2 rounded-full">
              <span className="text-purple-300 text-sm">🎓 Submitted for Pro Vice Chancellor's Prize</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-6xl mx-auto px-6 py-16">
        {/* What is Kessler Syndrome */}
        <section className="mb-20">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-blue-400 mb-6">What is Kessler Syndrome?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4 text-gray-200">
                <p>
                  Kessler Syndrome is a theoretical scenario where the density of objects in low Earth 
                  orbit becomes high enough that collisions between objects could cause a cascade effect.
                </p>
                <p>
                  Each collision generates debris, which increases the likelihood of further collisions, 
                  potentially rendering space activities and satellite usage impossible for generations.
                </p>
                <div className="bg-red-900/30 border-l-4 border-red-400 pl-4 py-3">
                  <p className="text-red-100">
                    At current growth rates, we could see cascading collisions begin within our lifetime, 
                    effectively trapping humanity on Earth.
                  </p>
                </div>
              </div>
              <div className="bg-gray-900 rounded-xl p-6">
                <h4 className="text-lg font-bold text-white mb-4">The Danger</h4>
                <ul className="space-y-3 text-gray-200">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">⚠️</span>
                    <span>No GPS navigation systems</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">⚠️</span>
                    <span>No weather satellites</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">⚠️</span>
                    <span>No space exploration</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">⚠️</span>
                    <span>Limited telecommunications</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">⚠️</span>
                    <span>No ISS or future stations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Dashboard Features */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Dashboard Features</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
              <div className="text-3xl mb-4">📈</div>
              <h3 className="text-lg font-bold text-white mb-2">Growth Projections</h3>
              <p className="text-gray-300 text-sm">Visualize debris accumulation from 2025-2100 under different scenarios</p>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
              <div className="text-3xl mb-4">🌍</div>
              <h3 className="text-lg font-bold text-white mb-2">Country Responsibility</h3>
              <p className="text-gray-300 text-sm">See which nations have contributed most to orbital debris</p>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
              <div className="text-3xl mb-4">🌙</div>
              <h3 className="text-lg font-bold text-white mb-2">Night Sky Impact</h3>
              <p className="text-gray-300 text-sm">How debris affects astronomical observations</p>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
              <div className="text-3xl mb-4">⚙️</div>
              <h3 className="text-lg font-bold text-white mb-2">Interactive Parameters</h3>
              <p className="text-gray-300 text-sm">Adjust launch rates (100-500/year) and cleanup efforts (0.5%-5%)</p>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
              <div className="text-3xl mb-4">📊</div>
              <h3 className="text-lg font-bold text-white mb-2">Historical Data</h3>
              <p className="text-gray-300 text-sm">GCAT catalog data showing accumulation since 1957</p>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
              <div className="text-3xl mb-4">🔬</div>
              <h3 className="text-lg font-bold text-white mb-2">NASA Model</h3>
              <p className="text-gray-300 text-sm">Based on NASA Orbital Debris Program collision parameters</p>
            </div>
          </div>
        </section>

        {/* The Project */}
        <section className="mb-20">
          <div className="bg-gray-800 border border-blue-500/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-blue-400 mb-6">About This Project</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-white mb-4">The Goal</h3>
                <p className="text-gray-200 mb-4">
                  This dashboard was created to make the complex topic of space debris accessible 
                  to everyone. By visualizing the problem interactively, users can understand how 
                  their own assumptions about launch rates and cleanup efforts affect humanity's 
                  future in space.
                </p>
                <p className="text-gray-200">
                  The simulation allows users to compare best-case, worst-case, and balanced 
                  scenarios, helping illustrate why international cooperation on space debris 
                  is so critical.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Key Insights</h3>
                <ul className="space-y-3 text-gray-200">
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">→</span>
                    <span>Even small cleanup efforts can significantly slow debris growth</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">→</span>
                    <span>Unchecked growth could make LEO unusable by 2100</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">→</span>
                    <span>The problem is solvable with coordinated international action</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">→</span>
                    <span>Every launch decision today affects generations to come</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Built With</h2>
          
          <div className="flex flex-wrap justify-center gap-4">
            {['Python', 'Streamlit', 'Plotly', 'Pandas', 'NumPy', 'NASA Data'].map((tech) => (
              <span
                key={tech}
                className="px-6 py-3 bg-gray-800 border border-gray-700 rounded-full text-gray-200 hover:border-blue-500/50 transition-all"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* Team & Course Info */}
        <section>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-400 mb-6">Team</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center font-bold">
                    AS
                  </div>
                  <div>
                    <p className="font-medium text-white">Arunav Saha</p>
                    <p className="text-gray-300 text-sm">Developer</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center font-bold">
                    FP
                  </div>
                  <div>
                    <p className="font-medium text-white">Farhan Ishrak Probandho</p>
                    <p className="text-gray-300 text-sm">Developer</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-purple-400 mb-6">Project Details</h3>
              <ul className="space-y-4 text-gray-200">
                <li className="flex items-start">
                  <span className="text-purple-400 mr-2">🎓</span>
                  <span>Submitted for Pro Vice Chancellor's Prize for Undergraduate Research</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-2">📚</span>
                  <span>COMP 4304: Data Visualization - Memorial University</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">📅</span>
                  <span>Completed: April 2025</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-gray-950 border-t border-gray-800 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-300 mb-4">
            Kessler Syndrome Dashboard — Visualizing Humanity's Space Debris Challenge
          </p>
          <div className="flex justify-center gap-6">
            <a
              href="https://814-data.streamlit.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              Launch Dashboard →
            </a>
            <a
              href="https://github.com/ARNAB814/Data_Visualization_Project"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Source Code →
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KesslerShowcase;